import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { colors, font } from "../styles/theme";
import { services } from "../data/services";
import ServiceIcon from "./ServiceIcon";

// Лента иконок услуг (белый фон, чёрные иконки). При наведении — поповер с описанием.
export default function ServicesStrip({ style }) {
  const [hover, setHover] = useState(null);
  const ref = useRef(null);

  // Закрываем поповер, когда лента иконок ушла из поля зрения (проскроллили ниже),
  // и при тапе вне ленты.
  useEffect(() => {
    if (hover == null) return;
    const el = ref.current;
    const onDown = (e) => {
      if (el && !el.contains(e.target)) setHover(null);
    };
    document.addEventListener("pointerdown", onDown);

    let observer;
    if (el && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) setHover(null);
        },
        { threshold: 0 }
      );
      observer.observe(el);
    }
    return () => {
      document.removeEventListener("pointerdown", onDown);
      if (observer) observer.disconnect();
    };
  }, [hover]);

  return (
    <div
      ref={ref}
      className="services-strip"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))",
        gap: 12,
        ...style,
      }}
    >
      {services.map((svc, i) => {
        const open = hover === svc.num;
        return (
          <div
            key={svc.num}
            className="hi-icon"
            onMouseEnter={() => setHover(svc.num)}
            onMouseLeave={() => setHover(null)}
            style={{
              "--i": i,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 11,
              textAlign: "center",
              padding: "8px 4px",
              cursor: "pointer",
              borderRadius: 12,
              background: open ? "#eef0f3" : "transparent",
              transition: "background .16s",
            }}
          >
            <ServiceIcon name={svc.icon} size={42} color={colors.ink} />
            <span
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: "#3a424c",
                lineHeight: 1.2,
              }}
            >
              {svc.name}
            </span>

            <div
              className={"svc-popover" + (open ? " svc-popover-anim" : "")}
              style={{
                display: open ? "block" : "none",
                position: "absolute",
                left: "50%",
                bottom: "100%",
                width: 320,
                maxWidth: "80vw",
                paddingBottom: 14,
                textAlign: "left",
                zIndex: 30,
                opacity: open ? 1 : 0,
                pointerEvents: open ? "auto" : "none",
                transform: "translateX(-50%)",
                transition: "opacity .2s ease",
              }}
            >
              <Popover svc={svc} onClose={() => setHover(null)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Popover({ svc, onClose }) {
  return (
    <div
      style={{
        background: "#fff",
        color: colors.ink,
        borderRadius: 14,
        boxShadow: "0 22px 60px rgba(0,0,0,.28)",
        border: "1px solid #eceef2",
        padding: "22px 22px 20px",
        position: "relative",
      }}
    >
      {/* Крестик — только на мобильном (класс popover-close) */}
      <button
        className="popover-close"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose && onClose();
        }}
        aria-label="Закрыть"
        style={{
          display: "none",
          position: "absolute",
          top: 10,
          right: 10,
          width: 34,
          height: 34,
          border: "none",
          borderRadius: 9,
          background: "#f1f2f5",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 2,
        }}
      >
        <X size={18} color={colors.ink} />
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
        <div
          style={{
            flex: "none",
            width: 54,
            height: 54,
            borderRadius: 13,
            background: colors.redSoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ServiceIcon name={svc.icon} size={30} color={colors.red} />
        </div>
        <div>
          <div
            style={{
              fontFamily: font.display,
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: colors.red,
            }}
          >
            Услуга {svc.num}
          </div>
          <div
            style={{
              fontFamily: font.display,
              fontWeight: 700,
              fontSize: 20,
              textTransform: "uppercase",
              color: colors.ink,
              lineHeight: 1.05,
              marginTop: 3,
            }}
          >
            {svc.name}
          </div>
        </div>
      </div>
      <p style={{ margin: "14px 0 0", fontSize: 15, lineHeight: 1.55, color: colors.text }}>
        {svc.desc}
      </p>
      <Link
        to={`/uslugi?svc=${svc.num}`}
        style={{
          marginTop: 16,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: font.display,
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: colors.red,
          textDecoration: "none",
        }}
      >
        Подробнее <ArrowRight size={16} color={colors.red} />
      </Link>
      <span
        className="popover-arrow"
        style={{
          position: "absolute",
          left: "50%",
          bottom: -8,
          transform: "translateX(-50%) rotate(45deg)",
          width: 16,
          height: 16,
          background: "#fff",
          borderRight: "1px solid #eceef2",
          borderBottom: "1px solid #eceef2",
        }}
      />
    </div>
  );
}
